/**
 * Copy for home layouts W ("App first"), X ("Just type") and Y ("The sheet").
 *
 * Every claim here is checked against ET_BEHAVIOR_SPEC.md, the shipped phone app (1.8) and the
 * live pricing page. No em-dashes (Isaac's rule). No YouTube-link promise on upload: the server
 * can't fetch YouTube reliably, so upload copy talks about files only.
 */

import { FILM_ROWS } from "../components/home3/filmData";

export const APP_STORE_URL = "https://apps.apple.com/us/app/cherryclipz/id6810239977";
export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.cherryclipz.app";
export const GET_APP_URL = "https://cherryclipz.com/get";
/** The same film as the demos, as a public share of the REAL editor (a curated sample in demoSource.ts). */
// The Film Reel sample, a public share of the real editor. It used to open The Other Side in
// full, which includes the lines about Isaac's father leaving (never shown in a demo, 2026-10-25).
export const REAL_EDITOR_URL = "https://cherryclipz.com/s/pSG_9ZJzNcGeg06EMmIBDQhSPULYEpnoVyYQTM5LJUU";

/** Store links tagged per placement, so downloads from the site can be counted (Play reads the referrer;
 * Apple reads ct, and also needs the provider token pt from App Store Connect for campaign stats). */
export const storeLink = (url: string, tag: string): string =>
  url.includes("apple.com")
    ? `${url}?ct=${encodeURIComponent(tag)}&mt=8`
    : `${url}&referrer=${encodeURIComponent(`utm_source=cherryclipz.com&utm_medium=home&utm_campaign=${tag}`)}`;

export const NAV = {
  how: "How it works",
  phone: "Phone app",
  web: "Long videos",
  ai: "Ask AI",
  everywhere: "Works with",
  pricing: "Pricing",
  signIn: "Sign in",
  library: "My library",
  getApp: "Get the app",
};

export const BADGES = {
  scanTitle: "Scan to get the app",
  scanNote: "Opens the App Store or Google Play",
  free: "Free to download on iPhone and Android",
};

export const SOUND = { on: "Tap for sound", off: "Sound on" };

export const SHEET = {
  real: "Real film, real transcript.",
  openReal: "Try the real editor on a sample film ↗",
  try: "Search a word, tick a few lines, press Play.",
  source: `The Other Side · short documentary · 8:45 · ${FILM_ROWS.length} clips`,
  search: "Search what was said. Try “young”",
  speaker: "Speaker",
  mood: "Mood",
  sort: "Sort",
  time: "Time",
  length: "Length",
  moods: { p: "Positive", n: "Neutral", g: "Negative" },
  cols: ["Pick", "Clip", "Spkr", "Mood", "Transcript", "Sec", "In · Out"],
  idle: "Click any line to play it",
  nothing: "Nothing playing",
  done: "Done",
  yourVideo: "Your video",
  clear: "Clear",
  emptyPicks: "Tick lines in the order you want them. They play as one video.",
  playPicks: "Play picks",
  export: "Export",
  noMatch: (q: string) => `No line says “${q}”. Try another word.`,
};

export const AI = {
  placeholder: "Ask for any cut of your video…",
  steps: ["Opening your library", `Reading all ${FILM_ROWS.length} lines`, "Picking clips", "Ticking them in your sheet"],
  stepsDone: (n: number) => ["Found The Other Side", `Read all ${FILM_ROWS.length} lines`, `Picked ${n} clips`, "Ticked in your sheet"],
  reply: (n: number, len: string, say: string) => `Here is your cut: ${n} clips, ${len}. ${say} Swap any line you like, then say “export it”.`,
  playCut: "Play this cut",
  yourSheet: "Your sheet",
  live: "Live",
  waiting: "The picks appear here as the AI makes them",
  note: "This demo knows the four asks above. Connect your own Claude or ChatGPT to ask anything about your videos.",
  unknown: "This demo only knows the four asks below. Connect your own Claude or ChatGPT and you can ask anything about your videos.",
  swapped: (n: number) => `You swapped clip ${n}. The AI drafts, you decide.`,
  you: "You",
  prompts: [
    { chip: "30-second trailer", ask: "Cut a 30-second trailer from The Other Side.", ids: [2, 7, 14, 26, 40, 41, 62, 97, 100], swap: [26, 27], say: "Opens on the dream, rises with his art, ends on what is left of him.", keys: ["trailer", "30", "best", "story", "film"] },
    { chip: "Only the happy memories", ask: "Only the happy memories, in the order they happen.", ids: [7, 8, 19, 22, 25, 27, 28, 29], swap: [8, 30], say: "Every line here is marked positive.", keys: ["happy", "positive", "joy", "fun", "good", "smile"] },
    { chip: "15-second hook for TikTok", ask: "Give me a 15-second hook for TikTok.", ids: [5, 62, 70, 71, 100], swap: [70, 65], say: "Starts on the obsession, turns on the new name, ends on what is left of him.", keys: ["hook", "tiktok", "15", "reel", "short", "viral", "instagram"] },
    { chip: "Every time they say “father”", ask: "Find every time they talk about his father.", ids: [2, 5, 6, 7, 40, 62, 91], swap: [6, 26], say: "Seven lines, in the order they were said.", keys: ["father", "dad", "papa", "parent", "every time"] },
  ],
};

export const PLANS = {
  eyebrow: "Pricing",
  title: "Free to start.",
  compare: "Compare every plan →",
  items: [
    { name: "Free", price: "$0", lines: ["1 free hour of transcription to start", "50 exports a month, up to 5 minutes each", "The full editor on the web"], cta: "Try it free", pro: false },
    { name: "Creator", price: "$10", lines: ["5 hours of transcription", "100 exports a month", "The web and the phone app"], cta: "Get Creator", pro: false },
    { name: "Pro", price: "$29", lines: ["15 hours of transcription", "500 exports a month", "The Premiere Pro panel, driven from your phone"], cta: "Go Pro", pro: true },
  ],
  perMonth: "/mo",
};

export const PHONE_STEPS = [
  { img: "/home/phone-1.jpg", glow: "#e61d50", title: "Every line is already a clip.", body: "Bring in a video from your phone. Each thing that was said becomes its own clip, with a thumbnail and a time." },
  { img: "/home/phone-4.jpg", glow: "#ff8a3d", title: "Tap to keep it.", body: "Tap the circle on the lines you want. They line up at the bottom in the order you picked them." },
  { img: "/home/phone-2.jpg", glow: "#8b5cf6", title: "Make the video.", body: "Wide or tall for Reels, TikTok and Shorts, with the words on the video." },
  { img: "/home/phone-3.jpg", glow: "#14b8a6", title: "Supercut any word.", body: "Search a word and get every time it was said, cut and lined up." },
  { img: "/home/phone-5.jpg", glow: "#e61d50", title: "Save it or share it.", body: "Straight to your photos, or to a friend." },
];

export const PLACES = [
  { key: "web", title: "The website", body: "The full spreadsheet in your browser. Search, filter, trim and export." },
  { key: "phone", title: "iPhone and Android", body: "Pick, caption and share from your phone." },
  { key: "ai", title: "Claude and ChatGPT", body: "Ask for a cut. The picks land in your sheet." },
  { key: "pr", title: "Premiere Pro panel", tag: "Pro", body: "Tick a line on your phone. It drops onto your Premiere timeline." },
  { key: "auto", title: "Auto-post", body: "Every export can post itself to YouTube through make.com." },
  { key: "link", title: "Share link", body: "Send the sheet to anyone. They can watch and pick without an account." },
];

export const LANGS = "Transcribed in 90+ languages, including Hebrew and Yiddish mixed with English in one recording.";

/* ---------------------------------------------------------------- W · App first */
export const W = {
  eyebrow: "Free to download on iPhone and Android",
  title1: "Your video is already cut.",
  title2: "Just cherrypick.",
  sub: "CherryClipz turns everything said in a video into clips. Tap the ones you want, put the words on screen, and share it.",
  webLink: "Long podcast or interview? Upload it on the web",
  phoneEyebrow: "On your phone",
  phoneTitle: "From video to post in three steps.",
  phoneSteps: [
    { title: "Pick", body: "Every line is already a clip. Tap the circle on the ones you want.", video: "/home/app-transcript", label: "A real recording of the app: scrolling the clips of an interview" },
    { title: "Trim, then share", body: "Nudge the start or the end, then send it anywhere.", video: "/home/app-pick-trim", label: "A real recording of the app: picking, trimming and sharing" },
    { title: "Words on screen", body: "Wide, or tall for Reels, TikTok and Shorts, with captions burned in.", img: "/home/phone-2.jpg", label: "Make the video: tall, with captions" },
  ],
  webEyebrow: "Long videos",
  webTitle: "Podcasts, interviews, films. Bring the long ones to the web.",
  webBody: "Upload a long recording and it becomes a spreadsheet of clips. Search what was said, filter by speaker or mood, and export the moments you tick.",
  uploadTitle: "Upload yours",
  uploadNote: "Drop a video or audio file. Your first hour is free.",
  tourEyebrow: "What it does",
  tourTitle: "Tick what you'd use. Press Play.",
  closeTitle1: "Every story has its cherries.",
  closeTitle2: "Pick yours.",
  closeWeb: "Or upload a long video on the web",
};

/* ---------------------------------------------------------------- the AI section (W and Y) */
export const AISEC = {
  eyebrow: "Works with Claude and ChatGPT",
  title1: "Your AI picks.",
  title2: "You stay in charge.",
  body: "Connect CherryClipz to Claude or ChatGPT and ask for a trailer, a hook, or every happy moment. It reads every line of your video and ticks its picks in your sheet, where you can see each one and swap what's wrong.",
  connectTitle: "Connect it in a couple of minutes",
  connect: [
    { t: "Add the connector", b: "In Claude or ChatGPT, add CherryClipz as a custom connector." },
    { t: "Sign in", b: "Use the email you use for CherryClipz." },
    { t: "Ask", b: "“Cut a trailer from my interview.” The clips tick in your open sheet." },
  ],
  fine: "Works with Claude and ChatGPT plans that allow custom connectors. It uses your own AI account, so there's no extra AI bill from us. Built on MCP, the open standard, so other AI tools can connect too.",
};

/* ---------------------------------------------------------------- X · Just type */
export const X = {
  eyebrow: "For podcasts, interviews and films",
  title1: "Type what you want.",
  title2: "Get the clips.",
  sub: "Upload a long video. Ask for a trailer, a hook, or every funny moment. CherryClipz finds the lines and cuts them.",
  upload: "Upload a video",
  orApp: "Or get the app",
  aiEyebrow: "Works with Claude and ChatGPT",
  aiTitle: "Use the AI you already have.",
  aiSteps: [
    { t: "Connect", b: "Add CherryClipz as a connector in Claude or ChatGPT." },
    { t: "Ask", b: "“Cut a trailer from my interview.” “Every time she says money.”" },
    { t: "Watch it pick", b: "The clips tick in your sheet as it works. Change any of them in one click." },
  ],
  phoneEyebrow: "iPhone and Android",
  phoneTitle: "On your phone, it's one tap a clip.",
  phoneBody: "The same clips on your phone. Tap the ones you want, make one video with captions, and share it.",
  handEyebrow: "Or pick every line yourself",
  handTitle: "It's a spreadsheet for video.",
  handBody: "Every sentence is a row with its speaker, mood and timecode. Search it, sort it, tick it.",
  hubEyebrow: "One upload",
  hubTitle: "Seven places it goes.",
  hubBody: "Your phone, the web, Claude, ChatGPT, your Premiere timeline, and straight to YouTube.",
  closeTitle: "What do you want to make?",
};

/* ---------------------------------------------------------------- Y · The sheet */
export const Y = {
  eyebrow: "The spreadsheet for video",
  title1: "Every sentence",
  title2: "is a ",
  titleHl: "clip.",
  sub: "CherryClipz turns any video into a spreadsheet of clips. Search what was said, tick the moments you want, and export them as one video.",
  getApp: "Get the app. It's free to download.",
  upload: "Or upload a long video on the web",
  tour: "Watch the 45-second tour",
  phoneEyebrow: "iPhone and Android",
  phoneTitle: "The same sheet, in your pocket.",
  phoneBody: "Tap a clip to watch it. Tap the circle to keep it. Then make one video, with the words on screen, and share it.",
  diffEyebrow: "Why it's different",
  diffTitle: "See every moment. Then pick.",
  diffBody: "Editors make you scrub. AI clip tools guess for you. CherryClipz cuts the whole video first, so every option is in front of you and the choice stays yours.",
  diffHead: ["", "Timeline editor", "AI clip tool", "CherryClipz"],
  diffRows: [
    ["Finding a line", "Scrub and listen", "It picks for you", "Search it like a sheet"],
    ["When a pick is wrong", "Keep scrubbing", "Run it again", "Untick it. Tick another."],
    ["Who decides", "You, slowly", "The AI", "You, or your AI, with every line in view"],
  ],
  startTitle: "Start with one video.",
  startBody: "Upload it, and every sentence is a clip you can search, pick and share.",
  closeTitle1: "Every story has its cherries.",
  closeTitle2: "Pick yours.",
};

/* ---------------------------------------------------------------- Z · The app (the page works like the app) */
export type ZWhere = "Phone" | "Web" | "AI" | "Pro";
export type ZFeature = { id: string; group: "phone" | "web" | "ai" | "pro"; title: string; body: string; where: ZWhere[]; soon?: boolean; media?: string; dur: number };

/** Under a ticked row on Z: where to use that feature. */
export const Z_MORE: Record<"phone" | "web" | "ai" | "pro", { label: string; href: string }> = {
  phone: { label: "Get the free app →", href: "/get" },
  web: { label: "Upload a video →", href: "#top" },
  ai: { label: "Connect Claude or ChatGPT →", href: "/ai" },
  pro: { label: "Get the Premiere panel →", href: "/plugin-download/" },
};

export const Z = {
  eyebrow: "Free to download on iPhone and Android",
  title1: "Every story has its cherries.",
  title2: "Pick yours.",
  sub: "This page works like the app. Each row is something CherryClipz does. Tap the circle on the ones you'd use, then press Play.",
  listTitle: (n: number) => `${n} things CherryClipz does`,
  search: "Search what it does",
  all: "Everything",
  selectAll: "Select all",
  deselectAll: "Deselect all",
  groups: { phone: "On your phone", web: "On the web", ai: "With AI", pro: "For pros" } as Record<ZFeature["group"], string>,
  groupCount: (n: number) => `${n} thing${n === 1 ? "" : "s"}`,
  soon: "Soon",
  clips: (n: number) => `${n} clip${n === 1 ? "" : "s"}`,
  play: "Play",
  getApp: "Get the app",
  clear: "Clear",
  emptyBar: "Tap a circle to start your reel",
  noMatch: (q: string) => `Nothing matches “${q}”.`,
  reelTitle: "Your CherryClipz reel",
  endTitle1: "Cherrypick",
  endTitle2: "your clipz.",
  replay: "Watch again",
  close: "Close",
  webTitle: "Got a long one?",
  webBody: "Podcasts, interviews and films go on the web. Upload it and every sentence becomes a clip you can pick.",
};

export const Z_PRESET = ["pick", "supercut", "chatgpt"];

export const Z_FEATURES: ZFeature[] = [
  { id: "phoneremote", group: "phone", title: "Free on iPhone and Android.", body: "The whole thing in your pocket. Download it, bring in a video, start picking.", where: ["Phone"], dur: 3.3 },
  { id: "lines", group: "phone", title: "Every line is already a clip.", body: "Everything that was said becomes its own clip, with a thumbnail and a time.", where: ["Phone", "Web"], dur: 6 },
  { id: "pick", group: "phone", title: "Tap the circle to cherrypick.", body: "The clips you keep line up at the bottom, in the order you picked them.", where: ["Phone", "Web"], dur: 5 },
  { id: "cutby", group: "phone", title: "Or cut it every 2, 5 or 10 seconds.", body: "No talking? Cut by time instead of by sentence.", where: ["Phone"], dur: 4.5 },
  { id: "nudge", group: "phone", title: "Trim with a little nudge.", body: "Start a bit earlier or end a bit later, one tap at a time.", where: ["Phone", "Web"], dur: 6 },
  { id: "search", group: "phone", title: "Search what was said.", body: "Type a word. A two-hour video shrinks to the lines that say it.", where: ["Phone", "Web"], dur: 6 },
  { id: "supercut", group: "phone", title: "Supercut any word.", body: "Every time it was said, cut and lined up in one tap.", where: ["Phone", "Web"], dur: 7 },
  { id: "filters", group: "phone", title: "Filter by who's talking, or how they feel.", body: "Tap a speaker or a mood and the list shows just those lines.", where: ["Phone", "Web"], dur: 6 },
  { id: "backtoback", group: "phone", title: "Play your picks back to back.", body: "Watch the whole cut before you make it.", where: ["Phone", "Web"], dur: 6 },
  { id: "realpeople", group: "phone", title: "Captions on the video.", body: "Burned in, word by word. Wide, or tall for Reels, TikTok and Shorts.", where: ["Phone", "Web"], dur: 4 },
  { id: "fixword", group: "phone", title: "Fix any word in the captions.", body: "Misheard a name? Type it right before you make the video.", where: ["Phone"], dur: 6 },
  { id: "captions", group: "phone", title: "Save it, or share it anywhere.", body: "To your photos, WhatsApp, Instagram, TikTok or YouTube.", where: ["Phone"], dur: 4.5 },
  { id: "languages", group: "phone", title: "Transcribed in 90+ languages.", body: "Hebrew and Yiddish mixed with English stay in one transcript.", where: ["Phone", "Web"], dur: 5 },
  { id: "translate", group: "phone", title: "Captions in another language.", body: "Translate the captions before you share. Coming in the next update.", where: ["Phone"], soon: true, dur: 7 },
  { id: "web", group: "web", title: "Upload hours. Get a spreadsheet.", body: "Podcasts, interviews, lectures, films. Every sentence becomes a row to search, sort and tick.", where: ["Web"], dur: 3.3 },
  { id: "documentary", group: "web", title: "Cut a documentary from your footage.", body: "Search your interviews and cherrypick the story.", where: ["Web"], dur: 8 },
  { id: "shorts", group: "web", title: "One film, a stack of shorts.", body: "Every line can be its own short, captions included.", where: ["Web", "Phone"], dur: 6 },
  { id: "autoclip", group: "web", title: "Auto-Clip in one tap.", body: "Ready-made cuts from a video in your library, no picking needed.", where: ["Web"], dur: 4 },
  { id: "export", group: "web", title: "Export the way you edit.", body: "MP4, MP3, GIF, subtitles (SRT, VTT) or a Premiere XML.", where: ["Web"], dur: 6 },
  { id: "chatgpt", group: "ai", title: "Ask Claude or ChatGPT to cut it.", body: "Connect CherryClipz once, then ask for a trailer or a hook. It reads every line of your video.", where: ["AI"], dur: 3.3 },
  { id: "aipick", group: "ai", title: "Watch it pick. Fix what's wrong.", body: "The AI's picks tick in your sheet as it works. Untick one, tick a better one.", where: ["AI", "Web"], dur: 3.4 },
  { id: "partners", group: "ai", title: "Uses the AI account you already have.", body: "Your own Claude or ChatGPT does the reading, so there's no extra AI bill from us.", where: ["AI"], media: "/home/anim-hub7", dur: 6 },
  { id: "premiere", group: "pro", title: "Premiere Pro panel.", body: "Tick a line on your phone and it lands on your Premiere timeline.", where: ["Pro"], dur: 3.3 },
  { id: "autopost", group: "pro", title: "Auto-post to YouTube.", body: "Hook up make.com once and every export can post itself.", where: ["Web"], dur: 4.5 },
];
